import { ReactNode, useMemo, useRef } from 'react';
import { InventorySKUListing } from '@views/StockBySKU/components/types';
import { ProductDetailsPanel } from './ProductDetailsPanel';
import { PalletDetailPanel } from './PalletDetailPanel';
import { SkuDetailPanel } from './SkuDetailPanel';
import { SkuMenuOption } from './SkuActionsMenu';
import { ProductGroupListing, productGroupKey } from './productGroups';
import { skuCode, skuThumbnailSrc } from './skuHelpers';
import {
  ConnectorGeometry,
  useHierarchyConnectors,
} from './useHierarchyConnectors';
import {
  ActivePanelEdit,
  PalletPanelDraft,
  PanelEditTarget,
  ProductPanelDraft,
  SkuPanelDraft,
  palletDraftOf,
  productDraftOf,
  skuDraftOf,
} from './panelEdit';

const connectorClasses =
  'pointer-events-none absolute left-2 w-4 rounded-bl-[3px] border-b border-l border-gray-300';

function HierarchyConnector({
  geometry,
}: {
  geometry: ConnectorGeometry | undefined;
}) {
  if (!geometry) return null;
  return (
    <span
      aria-hidden
      className={connectorClasses}
      style={{ top: geometry.top, height: Math.max(geometry.height, 0) }}
    />
  );
}

export interface ProductHierarchyProps {
  group: ProductGroupListing;
  activeEdit: ActivePanelEdit | null;
  onDraftChange: (
    draft: ProductPanelDraft | SkuPanelDraft | PalletPanelDraft
  ) => void;
  onStartEdit: (target: PanelEditTarget) => void;
  onAddPallet: (groupKey: string, sku: InventorySKUListing) => void;
  onRemovePallet: (
    groupKey: string,
    sku: InventorySKUListing,
    palletIndex: number
  ) => void;
  onAddSku: (group: ProductGroupListing) => void;
  onAddAdditionalField: (groupKey: string, sku: InventorySKUListing) => void;
  onCloneProductSkus: (group: ProductGroupListing) => void;
  onCloneHierarchy: (group: ProductGroupListing) => void;
  onArchiveSku: (sku: InventorySKUListing) => void;
  expandedPanelKeys: Set<string>;
  onTogglePanel: (panelKey: string) => void;
  canManageSkus: boolean;
  productImageSrc: string | null;
  productName: string;
}

export function ProductHierarchy({
  group,
  activeEdit,
  onDraftChange,
  onStartEdit,
  onAddPallet,
  onRemovePallet,
  onAddSku,
  onAddAdditionalField,
  onCloneProductSkus,
  onCloneHierarchy,
  onArchiveSku,
  expandedPanelKeys,
  onTogglePanel,
  canManageSkus,
  productImageSrc,
  productName,
}: ProductHierarchyProps) {
  const groupKey = productGroupKey(group);
  const productListRef = useRef<HTMLDivElement>(null);
  const skuListRef = useRef<HTMLDivElement>(null);

  const isEditingProduct =
    activeEdit?.kind === 'product' && activeEdit.groupKey === groupKey;

  const revision = useMemo(
    () =>
      [
        groupKey,
        group.skus.map(skuCode).join('|'),
        [...expandedPanelKeys].sort().join('|'),
        activeEdit
          ? [
              activeEdit.kind,
              activeEdit.kind === 'product' ? '' : activeEdit.inventorySkuId,
              activeEdit.kind === 'pallet' ? activeEdit.palletIndex : '',
            ].join(':')
          : 'none',
      ].join('#'),
    [groupKey, group.skus, expandedPanelKeys, activeEdit]
  );

  const productConnectors = useHierarchyConnectors(productListRef, revision);
  const skuConnectors = useHierarchyConnectors(skuListRef, revision);

  const productDraft = isEditingProduct
    ? (activeEdit.draft as ProductPanelDraft)
    : productDraftOf(group);

  const renderPallet = (
    sku: InventorySKUListing,
    palletIndex: number
  ): ReactNode => {
    const savedPallets = sku.palletising ?? [];
    const panelKey = `pallet-${sku.inventorySkuId}-${palletIndex}`;
    const isDraftOnly = palletIndex >= savedPallets.length;
    const isEditingPallet =
      activeEdit?.kind === 'pallet' &&
      activeEdit.inventorySkuId === sku.inventorySkuId &&
      activeEdit.palletIndex === palletIndex;
    const draft = isEditingPallet
      ? (activeEdit.draft as PalletPanelDraft)
      : palletDraftOf(savedPallets[palletIndex]);
    const menuOptions: SkuMenuOption[] = canManageSkus
      ? [
          {
            label: 'Edit',
            action: () =>
              onStartEdit({
                kind: 'pallet',
                groupKey,
                inventorySkuId: sku.inventorySkuId,
                palletIndex,
              }),
          },
          {
            label: 'Add pallet type',
            action: () => onAddPallet(groupKey, sku),
          },
          { label: 'Clone', action: () => onCloneHierarchy(group) },
          {
            label: 'Remove',
            action: () => onRemovePallet(groupKey, sku, palletIndex),
          },
        ]
      : [];

    return (
      <PalletDetailPanel
        position={palletIndex + 1}
        draft={draft}
        onDraftChange={onDraftChange}
        isEditing={isEditingPallet}
        isExpanded={isDraftOnly || expandedPanelKeys.has(panelKey)}
        onToggleExpand={() => onTogglePanel(panelKey)}
        menuId={`${skuCode(sku)}-pallet-${palletIndex + 1}`}
        menuOptions={isDraftOnly ? [] : menuOptions}
      />
    );
  };

  return (
    <div
      className='relative py-2 pl-14 pr-2'
      ref={productListRef}
      data-connector-list
    >
      <div className='relative pl-6' data-connector-key={`product-${groupKey}`}>
        <HierarchyConnector
          geometry={productConnectors[`product-${groupKey}`]}
        />
        <ProductDetailsPanel
          draft={productDraft}
          onDraftChange={onDraftChange}
          isEditing={isEditingProduct}
          imageSrc={productImageSrc}
          productName={productName}
          canManageSkus={canManageSkus}
          onEdit={() => onStartEdit({ kind: 'product', groupKey })}
        />
      </div>

      <div className='relative mt-2 pl-6' ref={skuListRef} data-connector-list>
        {group.skus.map((sku, skuIndex) => {
          const skuPanelKey = `sku-${sku.inventorySkuId}`;
          const isEditingSku =
            activeEdit?.kind === 'sku' &&
            activeEdit.inventorySkuId === sku.inventorySkuId;
          const draftPalletIndex =
            activeEdit?.kind === 'pallet' &&
            activeEdit.inventorySkuId === sku.inventorySkuId
              ? activeEdit.palletIndex
              : null;
          const hasDraftPallet =
            draftPalletIndex !== null &&
            draftPalletIndex >= (sku.palletising?.length ?? 0);

          const startEditingSku = () =>
            onStartEdit({
              kind: 'sku',
              groupKey,
              inventorySkuId: sku.inventorySkuId,
            });

          const skuMenuOptions: SkuMenuOption[] = canManageSkus
            ? [
                { label: 'Edit', action: startEditingSku },
                { label: 'Add SKU', action: () => onAddSku(group) },
                { label: 'Clone', action: () => onCloneProductSkus(group) },
                {
                  label: sku.isArchived ? 'Unarchive' : 'Archive',
                  action: () => onArchiveSku(sku),
                },
              ]
            : [];

          return (
            <div
              key={sku.inventorySkuId}
              className='relative mt-2 pl-7'
              data-connector-key={skuPanelKey}
            >
              <HierarchyConnector geometry={skuConnectors[skuPanelKey]} />
              <SkuDetailPanel
                position={skuIndex + 1}
                sku={sku}
                draft={
                  isEditingSku
                    ? (activeEdit.draft as SkuPanelDraft)
                    : skuDraftOf(sku)
                }
                onDraftChange={onDraftChange}
                isEditing={isEditingSku}
                isExpanded={expandedPanelKeys.has(skuPanelKey)}
                onToggleExpand={() => onTogglePanel(skuPanelKey)}
                imageSrc={skuThumbnailSrc(sku)}
                isArchived={sku.isArchived}
                menuId={skuCode(sku)}
                menuOptions={skuMenuOptions}
                canManageSkus={canManageSkus}
                onEdit={startEditingSku}
                onAddPallet={() => onAddPallet(groupKey, sku)}
                onAddAdditionalField={() => onAddAdditionalField(groupKey, sku)}
                renderPalletStep={(palletIndex) =>
                  renderPallet(sku, palletIndex)
                }
                draftPallet={
                  hasDraftPallet ? renderPallet(sku, draftPalletIndex) : null
                }
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
